import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { getPropertyBookingStats } from '../helpers/propertyBookingStats';
import './properties.css';

const PropertiesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [properties, setProperties] = useState([]);
    const [archivedProperties, setArchivedProperties] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const statsYear = new Date().getFullYear();

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(Number(value) || 0);

    const load = async () => {
        setLoading(true);
        setLoadError('');
        try {
            const [propertyRes, bookingRes, archivedRes] = await Promise.all([
                api.get('/property'),
                api.get('/booking'),
                api.get('/property/archived')
            ]);

            setProperties(Array.isArray(propertyRes?.data) ? propertyRes.data : []);
            setBookings(Array.isArray(bookingRes?.data) ? bookingRes.data : []);
            setArchivedProperties(Array.isArray(archivedRes?.data) ? archivedRes.data : []);
        } catch (error) {
            console.error('failed to load properties', error);
            setProperties([]);
            setArchivedProperties([]);
            setBookings([]);
            setLoadError('Unable to load properties right now. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const restoreProperty = async (propertyId, propertyName) => {
        const confirmed = window.confirm(`Restore ${propertyName || 'this property'} to active properties?`);
        if (!confirmed) return;

        try {
            await api.post(`/property/${propertyId}/restore`);
            setInviteMessage(`Restored ${propertyName || 'property'} successfully.`);
            await load();
        } catch (error) {
            setInviteMessage(error?.response?.data?.error || 'Failed to restore property.');
        }
    };

    const archiveProperty = async (propertyId, propertyName) => {
        const confirmed = window.confirm(`Archive ${propertyName || 'this property'}? This removes it from active views but keeps data for recovery.`);
        if (!confirmed) return;

        try {
            await api.delete(`/property/${propertyId}`);
            setInviteMessage(`Archived ${propertyName || 'property'} successfully.`);
            await load();
        } catch (error) {
            setInviteMessage(error?.response?.data?.error || 'Failed to archive property.');
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = String(params.get('cohostInviteToken') || '').trim();
        if (!token) return;

        const accept = async () => {
            let redirectedToOnboarding = false;
            try {
                const response = await api.post('/property/cohost-invites/accept', { token });
                const payload = response?.data || {};
                await load();
                if (Number(payload.propertyId) > 0) {
                    redirectedToOnboarding = true;
                    navigate(`/CohostOnboarding/${payload.propertyId}`, {
                        replace: true,
                        state: payload
                    });
                    return;
                }
                setInviteMessage('Co-host invite accepted. Your property access has been updated.');
            } catch (e) {
                setInviteMessage(e?.response?.data?.error || 'Failed to accept co-host invite token.');
            } finally {
                const paramsAfter = new URLSearchParams(location.search);
                if (!redirectedToOnboarding && paramsAfter.get('cohostInviteToken')) {
                    navigate('/Properties', { replace: true });
                }
            }
        };

        accept();
    }, [location.search]);

    const bookingsByProperty = useMemo(() => {
        return bookings.reduce((map, booking) => {
            const propertyId = Number(booking.propertyId);
            const existing = map.get(propertyId) || [];
            existing.push(booking);
            map.set(propertyId, existing);
            return map;
        }, new Map());
    }, [bookings]);

    const statsByProperty = useMemo(() => {
        return properties.reduce((map, property) => {
            map.set(
                Number(property.id),
                getPropertyBookingStats(
                    bookingsByProperty.get(Number(property.id)) || [],
                    statsYear,
                    property.acquisitionDate
                )
            );
            return map;
        }, new Map());
    }, [bookingsByProperty, properties, statsYear]);


    return (
        <div className="properties-container">
            <div className="properties-quick-actions">
                <button className="properties-action-chip properties-action-chip-lg properties-action-chip-new" onClick={() => navigate('/Properties/new')}>
                    <span className="properties-action-kicker">Setup</span>
                    <strong>New Property</strong>
                    <span className="properties-action-caption">Add a property profile with address, costs, and operating details.</span>
                </button>
                <button className="properties-action-chip properties-action-chip-lg" onClick={() => navigate('/Investments')}>
                    <span className="properties-action-kicker">Portfolio</span>
                    <strong>Investments</strong>
                    <span className="properties-action-caption">Review ownership contributions and long-term capital allocations.</span>
                </button>
                <button className="properties-action-chip properties-action-chip-lg" onClick={() => navigate('/Pnl')}>
                    <span className="properties-action-kicker">Performance</span>
                    <strong>P&amp;L Report</strong>
                    <span className="properties-action-caption">Track income, expenses, and net operating performance trends.</span>
                </button>
                <button className="properties-action-chip properties-action-chip-lg" onClick={() => navigate('/Properties/utilities')}>
                    <span className="properties-action-kicker">Expenses</span>
                    <strong>Utilities</strong>
                    <span className="properties-action-caption">Enter water, gas, and energy amounts for every property in a given month.</span>
                </button>
                <button className="properties-action-chip properties-action-chip-lg" onClick={() => navigate('/Receipts')}>
                    <span className="properties-action-kicker">Documents</span>
                    <strong>Receipts</strong>
                    <span className="properties-action-caption">Manage uploaded receipts and supporting expense records.</span>
                </button>
            </div>

            <div className="properties-section-heading-row">
                <h3 className="properties-section-heading">Properties</h3>
                <span className="properties-section-meta">{properties.length} total</span>
            </div>

            {inviteMessage && (
                <div className="app-state-card app-state-info properties-state-card" role="status" aria-live="polite">
                    <p>{inviteMessage}</p>
                </div>
            )}

            {loading && (
                <div className="app-state-card app-state-info properties-state-card" role="status" aria-live="polite">
                    <p>Loading properties and booking stats...</p>
                </div>
            )}

            {!loading && loadError && (
                <div className="app-state-card app-state-error properties-state-card" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button className="btn-primary" onClick={load}>Retry</button>
                    </div>
                </div>
            )}

            {!loading && !loadError && properties.length === 0 && (
                <div className="app-state-card properties-state-card">
                    <p>No properties found yet. Start by creating your first property profile.</p>
                    <div>
                        <button className="btn-primary" onClick={() => navigate('/Properties/new')}>Create First Property</button>
                    </div>
                </div>
            )}

            {!loading && !loadError && properties.length > 0 && (
                <ul className="properties-list">
                {properties.map((p) => {
                    const stats = statsByProperty.get(Number(p.id)) || {};
                    const averageRevenue = ((Number(stats.occupancyRate) || 0) / 100) * (Number(stats.averageBookingNightAmount) || 0) * 30.25;
                    const monthlyRent = Number(p.rentCost) || 0;
                    const propertyItemClassName = [
                        'property-item',
                        averageRevenue < monthlyRent
                            ? 'property-item-negative'
                            : averageRevenue > monthlyRent * 1.5
                                ? 'property-item-positive'
                                : ''
                    ].filter(Boolean).join(' ');
                    return (
                        <li
                            key={p.id}
                            onClick={() => navigate(`/PropertyDetails/${p.id}`)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(`/PropertyDetails/${p.id}`);
                                }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Open ${p.name} details`}
                            className={propertyItemClassName}
                        >
                            <div className="property-item-header">
                                <div>
                                    <h4>{p.name}</h4>
                                    <p>{p.city}, {p.state}</p>
                                    {p.neighborhood && <p>{p.neighborhood}</p>}
                                </div>
                                <div className="property-item-header-actions">
                                    <span className="property-item-pill">View Details</span>
                                    <button
                                        type="button"
                                        className="property-item-archive-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            archiveProperty(p.id, p.name);
                                        }}
                                    >
                                        Archive
                                    </button>
                                </div>
                            </div>
                            <ul className="property-item-stats-list">
                                <li className="property-item-stats-list-item">
                                    <span className="property-stat-label">Occupancy ({statsYear})</span>
                                    <strong>{(Number(stats.occupancyRate) || 0).toFixed(1)}%</strong>
                                </li>
                                <li className="property-item-stats-list-item">
                                    <span className="property-stat-label">Avg Booking/Night</span>
                                    <strong>{formatCurrency(stats.averageBookingNightAmount)}</strong>
                                </li>
                                <li className="property-item-stats-list-item">
                                    <span className="property-stat-label">Average Revenue</span>
                                    <strong>{formatCurrency(averageRevenue)}</strong>
                                </li>
                                <li className="property-item-stats-list-item">
                                    <span className="property-stat-label">Monthly Rent</span>
                                    <strong>{formatCurrency(p.rentCost)}</strong>
                                </li>
                            </ul>
                        </li>
                    );
                })}
                </ul>
            )}

            {!loading && !loadError && (
                <>
                    <div className="properties-section-heading-row properties-archived-heading-row">
                        <h3 className="properties-section-heading">Archived Properties</h3>
                        <span className="properties-section-meta">{archivedProperties.length} archived</span>
                    </div>
                    {archivedProperties.length === 0 ? (
                        <div className="app-state-card properties-state-card properties-archived-empty">
                            <p>No archived properties.</p>
                        </div>
                    ) : (
                        <ul className="properties-list properties-archived-list">
                            {archivedProperties.map((property) => (
                                <li key={`archived-${property.id}`} className="property-item property-item-archived">
                                    <div className="property-item-header">
                                        <div>
                                            <h4>{property.name}</h4>
                                            <p>{property.city}, {property.state}</p>
                                            {property.archivedAt && <p>Archived: {new Date(property.archivedAt).toLocaleDateString()}</p>}
                                        </div>
                                        <button
                                            type="button"
                                            className="property-item-restore-btn"
                                            onClick={() => restoreProperty(property.id, property.name)}
                                        >
                                            Restore
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}

        </div>
    );
};

export default PropertiesPage;
